import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { I } from './Icons';

const API = process.env.REACT_APP_BACKEND_URL;
const ICON = { portals_added: 'lead', portals_completed: 'file', lodgements: 'send2', approvals: 'badge', settlements: 'key2' };
const BADGE = { hit: 'Hit', on_track: 'On track', behind: 'Behind', no_target: 'No goal' };

// Today / this-week pace vs Set Goals targets, refreshed every minute.
const usePace = (token) => {
  const [pace, setPace] = useState(null);
  const load = useCallback(() => {
    if (!token) return;
    axios.get(`${API}/api/home/pace`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setPace(r.data)).catch(() => {});
  }, [token]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  return pace;
};

const Cell = ({ p, testid }) => {
  const pct = p.target ? Math.min(100, Math.round((p.done / p.target) * 100)) : 0;
  return (
    <div className={`pc-cell s-${p.status}`} data-testid={testid}>
      <div className="pc-num"><b>{p.done}</b>{p.target ? <span>/ {p.target}</span> : null}</div>
      <div className="pc-bar"><i style={{ width: `${pct}%` }} /></div>
      <div className="pc-foot">
        <span className="pc-badge" data-testid={`${testid}-status`}>{BADGE[p.status]}</span>
        {p.need > 0 && <span className="pc-need">{p.need} to go</span>}
      </div>
    </div>
  );
};

const insightFor = (pace) => {
  const its = pace.items.filter((x) => x.week.status !== 'no_target');
  if (!its.length) return null;
  const worst = [...its].sort((a, b) => (b.week.expected - b.week.done) - (a.week.expected - a.week.done))[0];
  if (worst.week.status === 'behind') {
    const d = pace.days_left_in_week;
    return { it: worst, text: <><b>Behind on {worst.label.toLowerCase()}</b> — {worst.week.done} done this week, should be at {Math.ceil(worst.week.expected)} by now. Need <b>{worst.week.need} more</b> {d ? `in the next ${d + (pace.business_day ? 1 : 0)} working days` : 'today'}.</> };
  }
  return { it: null, text: 'Every number is on pace for the week. Keep it moving.' };
};

export const PacePanel = ({ token, onGo, onAnalytics, onGoals }) => {
  const pace = usePace(token);
  const items = pace?.items || [];
  const behind = items.filter((x) => x.today.status === 'behind' || x.week.status === 'behind').length;
  const ins = pace && pace.target_set ? insightFor(pace) : null;
  return (
    <section className="perf pace rv" style={{ animationDelay: '.32s' }} aria-labelledby="perfTitle" data-testid="performance-panel">
      <div className="grid-bg" /><div className="sweep" />
      <div className="perf-head">
        <div className="perf-title">
          <h2 id="perfTitle">Are we on pace?</h2>
          <span className="live"><span className="ld" style={{ width: 6, height: 6 }} />LIVE</span>
          {pace && pace.target_set && <span className={`pc-sum ${behind ? 'bad' : 'good'}`} data-testid="pace-summary">{behind ? `Behind on ${behind} of ${items.length}` : 'All on pace'}</span>}
        </div>
        <a className="perf-link" href="/game-plan" onClick={(e) => { e.preventDefault(); onAnalytics(); }} data-testid="perf-full-analytics">Full analytics<I id="arrow" size={14} /></a>
      </div>
      <div className="pc-rows" data-testid="pace-rows">
        <div className="pc-head" aria-hidden="true"><span /><span>Today</span><span>This week</span></div>
        {!pace && <div className="pc-empty">Loading today's numbers…</div>}
        {items.map((it) => (
          <button type="button" key={it.key} className="pc-row" onClick={() => onGo(it.href)} data-testid={`pace-row-${it.key}`}>
            <span className="pc-label"><span className="kpi-ic"><I id={ICON[it.key]} /></span>{it.label}</span>
            <Cell p={it.today} testid={`pace-${it.key}-today`} />
            <Cell p={it.week} testid={`pace-${it.key}-week`} />
          </button>
        ))}
      </div>
      {pace && !pace.target_set && (
        <div className="insight" data-testid="perf-insight"><I id="target" /><span><b>No goals set for this month.</b> Set your settlement goal and daily and weekly targets are worked out for you.</span>
          <button type="button" onClick={onGoals} data-testid="pace-set-goals">Set goals<I id="arrow" size={14} /></button></div>
      )}
      {ins && (
        <div className="insight" data-testid="perf-insight"><I id="spark" /><span>{ins.text}</span>
          {ins.it && <button type="button" onClick={() => onGo(ins.it.href)} data-testid="perf-show-me">Show me<I id="arrow" size={14} /></button>}</div>
      )}
    </section>
  );
};
